import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useNavigate } from "@tanstack/react-router";
import { CreditCardIcon, SparklesIcon } from "lucide-react";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useSubscriptions } from "@/hooks/query/billing";
import * as m from "@/paraglide/messages";

type InsufficientCreditsDialogProps = {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	defaultOpen?: boolean;
	balance: number;
};

export function InsufficientCreditsDialog({
	open,
	onOpenChange,
	defaultOpen,
	balance,
}: InsufficientCreditsDialogProps) {
	const [stateOpen, setStateOpen] = useControllableState({
		prop: open,
		defaultProp: false,
		onChange: onOpenChange,
	});

	const navigate = useNavigate();

	const { data: subscriptions } = useSubscriptions();

	const isPaid = subscriptions?.some((s) => s.status === "active" || s.status === "trialing");

	return (
		<AlertDialog open={stateOpen} onOpenChange={setStateOpen} defaultOpen={defaultOpen}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogMedia className="bg-amber-100 text-amber-600">
						<SparklesIcon />
					</AlertDialogMedia>
					<AlertDialogTitle>{m.chat_insufficientCredits_title()}</AlertDialogTitle>
					<AlertDialogDescription>
						{isPaid
							? m.chat_insufficientCredits_descriptionPaid()
							: m.chat_insufficientCredits_descriptionFree()}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<div className="flex items-center justify-between rounded-lg border px-4 py-3">
					<span className="text-sm text-muted-foreground">
						{m.chat_insufficientCredits_balance()}
					</span>
					<span className="text-sm font-medium tabular-nums">
						{balance.toLocaleString()}
					</span>
				</div>
				<AlertDialogFooter>
					<AlertDialogCancel>{m.chat_insufficientCredits_close()}</AlertDialogCancel>
					<AlertDialogAction
						onClick={() => {
							navigate({ to: "/settings/subscription" });
						}}
					>
						{isPaid ? (
							<CreditCardIcon className="size-4 mr-1.5" />
						) : (
							<SparklesIcon className="size-4 mr-1.5" />
						)}
						{isPaid
							? m.chat_insufficientCredits_buyMore()
							: m.chat_insufficientCredits_upgrade()}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
